import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiPlus,
    FiEdit2,
    FiTrash2,
    FiArrowLeft,
    FiSave,
    FiPhone,
    FiMail,
    FiMapPin,
    FiSearch,
} from "react-icons/fi";
import inventoryService from "../../services/inventoryService";
import ConfirmModal from "../../components/common/ConfirmModal";

const EMPTY_FORM = {
    name: "",
    contactPerson: "",
    mobile: "",
    email: "",
    address: "",
    gstin: "",
    notes: "",
};

// ─── Form ─────────────────────────────────────────────────────────────────────
export function SupplierForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);

    useEffect(() => {
        if (!isEdit) return;
        inventoryService
            .getSupplierById(id)
            .then((res) => setForm({ ...EMPTY_FORM, ...res.data }))
            .catch((err) => {
                toast.error(err.message);
                navigate("/inventory/suppliers");
            })
            .finally(() => setFetching(false));
    }, [id, isEdit, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name) {
            toast.error("Supplier name is required.");
            return;
        }
        setLoading(true);
        try {
            if (isEdit) {
                const res = await inventoryService.updateSupplier(id, form);
                toast.success(res.message);
            } else {
                const res = await inventoryService.createSupplier(form);
                toast.success(res.message);
            }
            navigate("/inventory/suppliers");
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const set = (field) => (e) =>
        setForm((p) => ({ ...p, [field]: e.target.value }));

    if (fetching)
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading…
            </div>
        );

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        {isEdit ? "Edit Supplier" : "Add Supplier"}
                    </h1>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/inventory/suppliers")}
                    >
                        <FiArrowLeft size={14} /> Back
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        <FiSave size={14} /> {loading ? "Saving…" : "Save Supplier"}
                    </button>
                </div>
            </div>
            <form onSubmit={handleSubmit}>
                <div className="card">
                    <div className="card-header">Supplier Details</div>
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">
                                    Supplier Name{" "}
                                    <span style={{ color: "var(--danger)" }}>*</span>
                                </label>
                                <input
                                    className="form-control"
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder="e.g. National Scientific Suppliers"
                                    autoFocus
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Contact Person</label>
                                <input
                                    className="form-control"
                                    value={form.contactPerson}
                                    onChange={set("contactPerson")}
                                    placeholder="e.g. Mr. Raj Sharma"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Mobile</label>
                                <input
                                    className="form-control"
                                    value={form.mobile}
                                    onChange={set("mobile")}
                                    placeholder="10-digit number"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Email</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="supplier@example.com"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">GSTIN</label>
                                <input
                                    className="form-control"
                                    value={form.gstin}
                                    onChange={set("gstin")}
                                    placeholder="GST Number (optional)"
                                />
                            </div>
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Address</label>
                                <textarea
                                    className="form-control"
                                    rows={2}
                                    style={{ resize: "vertical" }}
                                    value={form.address}
                                    onChange={set("address")}
                                    placeholder="Supplier address…"
                                />
                            </div>
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Notes</label>
                                <textarea
                                    className="form-control"
                                    rows={2}
                                    style={{ resize: "vertical" }}
                                    value={form.notes}
                                    onChange={set("notes")}
                                    placeholder="Any additional notes…"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

// ─── List ─────────────────────────────────────────────────────────────────────
export function SupplierList() {
    const navigate = useNavigate();
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchSuppliers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await inventoryService.getSuppliers({ search });
            setSuppliers(res.data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await inventoryService.deleteSupplier(deleteTarget._id);
            toast.success(res.message);
            setDeleteTarget(null);
            fetchSuppliers();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Suppliers</h1>
                    <p className="page-subtitle">
                        {suppliers.length} supplier{suppliers.length !== 1 ? "s" : ""}
                    </p>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={() => navigate("/inventory/suppliers/new")}
                >
                    <FiPlus size={14} /> Add Supplier
                </button>
            </div>

            <div className="card">
                <div
                    style={{
                        padding: "0.75rem 1rem",
                        borderBottom: "1px solid var(--border)",
                        display: "flex",
                        gap: "0.75rem",
                    }}
                >
                    <div className="search-bar">
                        <FiSearch size={14} color="#9ca3af" />
                        <input
                            placeholder="Search by name or contact…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="loading-center">
                        <div className="spinner" />
                        Loading…
                    </div>
                ) : suppliers.length === 0 ? (
                    <div className="empty-state">
                        <p>No suppliers found.</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Supplier</th>
                                    <th>Contact</th>
                                    <th>GSTIN</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {suppliers.map((sup, i) => (
                                    <tr key={sup._id}>
                                        <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                            {i + 1}
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{sup.name}</div>
                                            {sup.address && (
                                                <div
                                                    style={{
                                                        fontSize: 11.5,
                                                        color: "var(--text-muted)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 3,
                                                    }}
                                                >
                                                    <FiMapPin size={10} /> {sup.address}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div style={{ fontSize: 13 }}>
                                                {sup.contactPerson || "—"}
                                            </div>
                                            {sup.mobile && (
                                                <div
                                                    style={{
                                                        fontSize: 11.5,
                                                        color: "var(--text-muted)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 3,
                                                    }}
                                                >
                                                    <FiPhone size={10} /> {sup.mobile}
                                                </div>
                                            )}
                                            {sup.email && (
                                                <div
                                                    style={{
                                                        fontSize: 11.5,
                                                        color: "var(--text-muted)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 3,
                                                    }}
                                                >
                                                    <FiMail size={10} /> {sup.email}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ fontSize: 13 }}>{sup.gstin || "—"}</td>
                                        <td>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: "0.35rem",
                                                    justifyContent: "flex-end",
                                                }}
                                            >
                                                <button
                                                    className="btn btn-outline btn-sm"
                                                    onClick={() =>
                                                        navigate(`/inventory/suppliers/${sup._id}/edit`)
                                                    }
                                                >
                                                    <FiEdit2 size={12} /> Edit
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => setDeleteTarget(sup)}
                                                >
                                                    <FiTrash2 size={12} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {deleteTarget && (
                <ConfirmModal
                    title="Delete Supplier"
                    message={`Delete "${deleteTarget.name}"?`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}
